/**
 * Create / Edit / Review-draft theme modal: skill-file editor, "Look for" example
 * passages and generate-from-examples.
 */
import { useEffect, useMemo, useState } from "react";
import { Box, Button, Dialog, IconButton, InputAdornment, Popover, TextField, Typography } from "@mui/material";
import type { ReactNode } from "react";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AddIcon from "@mui/icons-material/Add";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";

import TSPrimaryButton from "../../../../components/common/buttons/TSPrimaryButton";
import TSSecondaryButton from "../../../../components/common/buttons/TSSecondaryButton";
import {
  useQceDispatch,
  createTheme,
  updateTheme,
  attachClips,
  generateThemeFromExamples,
} from "../../../../store/state/qce/qceSlice";
import { qceErrMsg } from "../../../../store/state/qce/qceService";

import { CT_COLORS, ICON_KEYS, IconGlyph, bgForColor } from "./icons";
import { renderSkill, collapse } from "./renderSkill";
import ClipPickerModal from "./ClipPickerModal";
import { clipStoryId } from "./clipsApi";
import type { Exemplar, Theme, ThemeDraft, CmsClip, ClipAttach } from "./types";

interface ThemeModalProps {
  /** The theme to edit; null/undefined for create. */
  theme?: Theme | null;
  /** Prefill for create mode (from generate-from-examples). */
  draft?: ThemeDraft | null;
  /** Number of existing themes — used to pick a default color. */
  existingCount: number;
  /** Project a created theme belongs to; undefined/null = org-wide. Unused when editing. */
  projectId?: string | null;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
  toast: (msg: string, type?: "success" | "error") => void;
}

const HELP_COLOR = "#606971";

/** Outlined button matching the design-system secondary style, with icon + disabled support. */
const SecondaryBtn = ({
  text,
  onClick,
  disabled,
  startIcon,
}: {
  text: string;
  onClick: () => void;
  disabled?: boolean;
  startIcon?: ReactNode;
}) => (
  <Button
    onClick={onClick}
    disabled={disabled}
    variant="outlined"
    startIcon={startIcon}
    sx={{
      textTransform: "none",
      borderRadius: "4px",
      border: "1.5px solid #239B8B",
      color: "#187266",
      fontSize: "14px",
      fontWeight: 700,
      "&:hover": { border: "1.5px solid #239B8B" },
      "&:focus": { outline: "none" },
    }}
  >
    {text}
  </Button>
);

export default function ThemeModal({
  theme,
  draft,
  existingCount,
  projectId,
  onClose,
  onSaved,
  toast,
}: ThemeModalProps) {
  const dispatch = useQceDispatch();
  const src = theme ?? draft ?? null;

  const [name, setName] = useState(src?.name ?? "");
  const [description, setDescription] = useState(src?.description ?? "");
  const [notes, setNotes] = useState(theme?.notes ?? "");
  const [keywords, setKeywords] = useState(src?.keywords ?? "");
  const [icon, setIcon] = useState(src?.icon || "house");
  const [color, setColor] = useState(src?.color || CT_COLORS[existingCount % CT_COLORS.length].icon);
  const [exemplars, setExemplars] = useState<Exemplar[]>(() => (src?.exemplars ?? []).map((e) => ({ ...e })));
  // Exclusions are learned from feedback, not edited here — carried so the
  // preview and any hand-edit reflect them (no silent loss).
  const [exclusions] = useState(theme?.exclusions ?? []);

  const [skill, setSkill] = useState(src?.skill ?? "");
  const [showSkill, setShowSkill] = useState(false);
  const [fromSkill, setFromSkill] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [iconSearch, setIconSearch] = useState("");
  const [iconAnchor, setIconAnchor] = useState<HTMLElement | null>(null);
  const [colorAnchor, setColorAnchor] = useState<HTMLElement | null>(null);

  // CMS clips picked to attach on save (source="clip" codes).
  const [showClipPicker, setShowClipPicker] = useState(false);
  const [pendingClips, setPendingClips] = useState<CmsClip[]>([]);

  const generated = useMemo(
    () => renderSkill(name, description, keywords, exemplars, exclusions),
    [name, description, keywords, exemplars, exclusions]
  );

  useEffect(() => {
    if (!fromSkill) setSkill(generated);
  }, [generated, fromSkill]);

  const filteredIcons = useMemo(() => {
    const q = iconSearch.trim().toLowerCase();
    return q ? ICON_KEYS.filter((k) => k.toLowerCase().includes(q)) : ICON_KEYS;
  }, [iconSearch]);

  const addExemplar = () => setExemplars((prev) => [...prev, { text: "", rationale: "" }]);
  const updateExemplar = (i: number, patch: Partial<Exemplar>) =>
    setExemplars((prev) => prev.map((e, j) => (j === i ? { ...e, ...patch } : e)));
  const removeExemplar = (i: number) => setExemplars((prev) => prev.filter((_, j) => j !== i));

  const regenerateFromFields = () => {
    setSkill(generated);
    setFromSkill(false);
    toast("Skill file regenerated from the fields above", "success");
  };

  const generateFromExamples = async () => {
    const filled = exemplars.filter((e) => collapse(e.text));
    if (!filled.length) {
      toast("Add at least one example passage first", "error");
      return;
    }
    setGenerating(true);
    try {
      const d = await dispatch(generateThemeFromExamples(filled)).unwrap();
      if (d.name) setName(d.name);
      if (d.description) setDescription(d.description);
      if (d.keywords) setKeywords(d.keywords);
      if (d.exemplars?.length) setExemplars(d.exemplars.map((e) => ({ ...e })));
      setFromSkill(false);
      toast("Drafted a theme from your examples — review and save", "success");
    } catch (e: any) {
      toast(qceErrMsg(e, "Could not generate a theme"), "error");
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    if (!name.trim()) {
      toast("Theme needs a name", "error");
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        color,
        description,
        keywords,
        icon,
        exemplars: exemplars.filter((e) => collapse(e.text)),
        skill: fromSkill ? skill : generated,
        from_skill: fromSkill,
        notes,
        links: theme?.links ?? [],
      };
      let savedId = theme?.id;
      if (theme) await dispatch(updateTheme({ id: theme.id, body })).unwrap();
      else {
        const created = await dispatch(createTheme({ ...body, projectId })).unwrap();
        savedId = created.id;
      }
      if (pendingClips.length && savedId) {
        const clips: ClipAttach[] = pendingClips.map((c) => ({
          clip_id: c._id,
          story_id: clipStoryId(c),
          title: c.title,
          transcript: c.transcript,
          thumbnail_url: c.thumbnailURL,
          duration: c.duration,
          start: c.start,
          end: c.end,
        }));
        await dispatch(attachClips({ themeId: savedId, clips })).unwrap();
      }
      await onSaved();
      onClose();
      toast(theme ? "Theme updated" : "Theme created", "success");
    } catch (e: any) {
      toast(qceErrMsg(e, "Could not save the theme"), "error");
    } finally {
      setSaving(false);
    }
  };

  const title = theme ? "Edit theme" : draft ? "Review generated theme" : "Create new theme";
  const fieldLabelSx = { fontSize: "13px", fontWeight: 700, color: "#212427", mb: "4px" };
  const helpSx = { fontSize: "12px", color: HELP_COLOR, mb: "8px" };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
      <Box sx={{ p: "24px" }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb="16px">
          <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#212427" }}>{title}</Typography>
          <IconButton onClick={onClose} size="small" sx={{ "&:focus": { outline: "none" } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {draft && !theme && (
          <Box display="flex" alignItems="center" gap="6px" sx={{ ...helpSx, mb: "16px" }}>
            <AutoAwesomeIcon sx={{ fontSize: 15, color: "#1A7E70" }} />
            Drafted from your example passages. Edit anything below, then save.
          </Box>
        )}

        {/* Title + color + icon row */}
        <Box display="flex" gap="12px" alignItems="flex-end" mb="16px">
          <TextField
            label="Theme Title"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Leaving Home"
            fullWidth
            size="small"
            autoFocus
          />
          <Box
            onClick={(e) => setColorAnchor(e.currentTarget)}
            sx={pickerBtnSx}
            role="button"
            aria-label="Choose color"
          >
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                background: bgForColor(color),
                boxShadow: `inset 0 0 0 2px ${color}`,
              }}
            />
            <ArrowDropDownIcon fontSize="small" sx={{ color: HELP_COLOR }} />
          </Box>
          <Box onClick={(e) => setIconAnchor(e.currentTarget)} sx={pickerBtnSx} role="button" aria-label="Choose icon">
            <IconGlyph name={icon} color="#464C53" size={20} />
            <ArrowDropDownIcon fontSize="small" sx={{ color: HELP_COLOR }} />
          </Box>
        </Box>

        {/* Description */}
        <Box mb="16px">
          <Typography sx={fieldLabelSx}>Description</Typography>
          <Typography sx={helpSx}>What this theme is about and what kind of moments it captures.</Typography>
          <TextField
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Stories about why people left their countries…"
            fullWidth
            multiline
            minRows={2}
            size="small"
          />
        </Box>

        {/* Keywords */}
        <Box mb="16px">
          <Typography sx={fieldLabelSx}>Keywords</Typography>
          <Typography sx={helpSx}>Signal words or phrases that hint at this theme. Separate with commas.</Typography>
          <TextField
            value={keywords}
            onChange={(e) => setKeywords(e.target.value)}
            placeholder="belonging, home, roots, identity"
            fullWidth
            multiline
            minRows={1}
            size="small"
          />
        </Box>

        {/* Notes */}
        <Box mb="16px">
          <Typography sx={fieldLabelSx}>Notes</Typography>
          <Typography sx={helpSx}>Internal notes for your team about this theme. Not used by the AI.</Typography>
          <TextField
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Reviewed with the research team; revisit after round 2."
            fullWidth
            multiline
            minRows={2}
            size="small"
          />
        </Box>

        {/* Examples */}
        <Box mb="16px">
          <Typography sx={fieldLabelSx}>Examples</Typography>
          <Typography sx={helpSx}>
            Real passages that exemplify this theme. The AI treats these as positive examples and finds more like them.
          </Typography>
          {exemplars.map((ex, i) => (
            <Box key={i} display="flex" gap="8px" alignItems="flex-start" mb="8px">
              <Box flex={1} display="flex" flexDirection="column" gap="6px">
                <TextField
                  value={ex.text}
                  onChange={(e) => updateExemplar(i, { text: e.target.value })}
                  placeholder="Paste or type an example passage…"
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                />
                <TextField
                  value={ex.rationale || ""}
                  onChange={(e) => updateExemplar(i, { rationale: e.target.value })}
                  placeholder="Why it fits (optional)"
                  fullWidth
                  size="small"
                />
              </Box>
              <IconButton
                onClick={() => removeExemplar(i)}
                size="small"
                sx={{ color: "#B91C1C", mt: "4px", "&:focus": { outline: "none" } }}
                aria-label="Remove example"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Box display="flex" gap="8px" flexWrap="wrap" mt="4px">
            <SecondaryBtn text="Add example passage" onClick={addExemplar} startIcon={<AddIcon fontSize="small" />} />
            <Box display="flex" alignItems="center" gap="10px">
              <SecondaryBtn
                text={pendingClips.length ? `Example clips (${pendingClips.length})` : "Add example clips"}
                onClick={() => setShowClipPicker(true)}
                startIcon={<VideocamOutlinedIcon fontSize="small" />}
              />
              {pendingClips.length > 0 && (
                <Box display="flex" alignItems="center">
                  {pendingClips.slice(0, 3).map((c, i) => (
                    <Box
                      key={c._id}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "6px",
                        ml: i === 0 ? 0 : "-8px",
                        border: "2px solid #fff",
                        background: "#F1F2F3",
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                    >
                      {c.thumbnailURL && (
                        <Box
                          component="img"
                          src={c.thumbnailURL}
                          alt=""
                          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      )}
                    </Box>
                  ))}
                  {pendingClips.length > 3 && (
                    <Typography sx={{ fontSize: "12px", color: "#606971", ml: "6px" }}>
                      +{pendingClips.length - 3}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
            <SecondaryBtn
              text={generating ? "Filling in…" : "Fill in fields from examples"}
              onClick={generateFromExamples}
              disabled={generating}
              startIcon={<AutoAwesomeIcon fontSize="small" />}
            />
          </Box>
        </Box>

        {/* Skill file (advanced) */}
        <Box mb="16px">
          <Typography sx={fieldLabelSx}>Skill file (advanced)</Typography>
          <Typography sx={helpSx}>
            The exact markdown the AI reads. It updates live from the fields above. Open it to hand-edit for full
            control — your edits are parsed back into the fields on save.
          </Typography>
          {!showSkill ? (
            <>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: "12px",
                  background: "#F7F9FC",
                  border: "1px solid #D9DCDE",
                  borderRadius: "6px",
                  fontSize: "12px",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  whiteSpace: "pre-wrap",
                  maxHeight: 180,
                  overflow: "auto",
                  color: "#464C53",
                }}
              >
                {generated}
              </Box>
              <Box mt="8px">
                <SecondaryBtn text="Edit skill file" onClick={() => setShowSkill(true)} />
              </Box>
            </>
          ) : (
            <>
              <TextField
                value={skill}
                onChange={(e) => {
                  setSkill(e.target.value);
                  setFromSkill(true);
                }}
                fullWidth
                multiline
                minRows={12}
                size="small"
                spellCheck={false}
                InputProps={{
                  sx: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: "12px" },
                }}
              />
              {fromSkill && (
                <Typography sx={{ ...helpSx, color: "#B45309", mt: "8px" }}>
                  Editing the skill file directly — on save, the Definition, Keywords, Look-for and Exclusions sections
                  are parsed back into the fields. Content outside those sections is not kept.
                </Typography>
              )}
              <Box mt="8px">
                <SecondaryBtn
                  text="Regenerate from fields"
                  onClick={regenerateFromFields}
                  disabled={!fromSkill}
                  startIcon={<AutoAwesomeIcon fontSize="small" />}
                />
              </Box>
            </>
          )}
        </Box>

        <Typography sx={{ ...helpSx, mb: "16px" }}>
          After creating the theme, we will analyze the project using AI to find matching content.
        </Typography>

        <Box display="flex" alignItems="center" justifyContent="flex-end" gap="12px" flexWrap="wrap">
          <Box display="flex" gap="12px">
            <TSSecondaryButton text="Cancel" onClick={onClose} />
            <TSPrimaryButton
              text={theme ? "Save changes" : "Create Theme"}
              onClick={save}
              loading={saving}
              disabled={saving}
            />
          </Box>
        </Box>
      </Box>

      {showClipPicker && (
        <ClipPickerModal
          attachedIds={new Set(pendingClips.map((c) => c._id))}
          onClose={() => setShowClipPicker(false)}
          onDone={(clips) =>
            setPendingClips((prev) => {
              const byId: Record<string, CmsClip> = {};
              [...prev, ...clips].forEach((c) => {
                byId[c._id] = c;
              });
              return Object.values(byId);
            })
          }
        />
      )}

      {/* Color dropdown */}
      <Popover
        open={Boolean(colorAnchor)}
        anchorEl={colorAnchor}
        onClose={() => setColorAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ p: "12px", display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "8px", width: 220 }}>
          {CT_COLORS.map((c) => (
            <Box
              key={c.icon}
              onClick={() => {
                setColor(c.icon);
                setColorAnchor(null);
              }}
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: c.bg,
                boxShadow: `inset 0 0 0 3px ${c.icon}`,
                cursor: "pointer",
                outline: c.icon.toLowerCase() === color.toLowerCase() ? "2px solid #212427" : "none",
                outlineOffset: "2px",
              }}
            />
          ))}
        </Box>
      </Popover>

      {/* Icon dropdown */}
      <Popover
        open={Boolean(iconAnchor)}
        anchorEl={iconAnchor}
        onClose={() => setIconAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ p: "12px", width: 320 }}>
          <TextField
            value={iconSearch}
            onChange={(e) => setIconSearch(e.target.value)}
            placeholder="Search icons…"
            fullWidth
            size="small"
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Box
            sx={{
              mt: "12px",
              display: "grid",
              gridTemplateColumns: "repeat(6, 1fr)",
              gap: "6px",
              maxHeight: 260,
              overflow: "auto",
            }}
          >
            {filteredIcons.map((k) => (
              <IconButton
                key={k}
                onClick={() => {
                  setIcon(k);
                  setIconAnchor(null);
                }}
                title={k}
                sx={{
                  borderRadius: "8px",
                  border: k === icon ? "1.5px solid #1A7E70" : "1px solid transparent",
                  color: "#464C53",
                  "&:focus": { outline: "none" },
                }}
              >
                <IconGlyph name={k} size={18} />
              </IconButton>
            ))}
          </Box>
        </Box>
      </Popover>
    </Dialog>
  );
}

const pickerBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "4px",
  height: 40,
  px: "10px",
  border: "1px solid #D9DCDE",
  borderRadius: "6px",
  cursor: "pointer",
  flexShrink: 0,
  "&:hover": { borderColor: "#606971" },
};
